import { useEffect, useState, type ReactNode } from "react";
import { Cloud, Server, Inbox, X } from "lucide-react";
import { Modal, Chip, Loader, confirmDialog } from "@/shared/ui";
import {
  listBatches,
  cancelBatch,
  type RenderBatch,
  type RenderBatchStatus,
} from "@/features/render/renderApi";

const STATUS: Record<RenderBatchStatus, { label: string; tone: "gold" | "emerald" | "rose" | "neutral" }> = {
  running: { label: "rendering", tone: "gold" },
  queued: { label: "queued", tone: "neutral" },
  done: { label: "done", tone: "emerald" },
  // A batch whose jobs all finished but none succeeded. Without this it fell
  // back to the emerald "done" chip and a failed render read as a success.
  error: { label: "failed", tone: "rose" },
  canceled: { label: "canceled", tone: "rose" },
};

function relTime(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = s / 60;
  if (m < 60) return `${Math.floor(m)} min ago`;
  const h = m / 60;
  if (h < 24) return `${Math.floor(h)} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}

/**
 * Render queue — recent batches with live progress (doneJobs/totalJobs), polled
 * every 4s while open. Opened from the Topbar render-queue icon. Mirrors the
 * reference `.qrow` list with the shimmering `.prog` bar.
 */
export default function QueueModal({ onClose }: { onClose: () => void }) {
  const [batches, setBatches] = useState<RenderBatch[] | null>(null);

  useEffect(() => {
    let alive = true;
    const load = () =>
      listBatches()
        .then((b) => alive && setBatches(b))
        .catch(() => alive && setBatches([]));
    load();
    const t = setInterval(load, 4000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const rendering = batches?.filter((b) => b.status === "running").length ?? 0;
  const queued = batches?.filter((b) => b.status === "queued").length ?? 0;

  // Cancel a running/queued batch (server stops all non-terminal jobs). Optimistic
  // — the 4s poll reconciles if the request fails.
  const cancel = async (id: string) => {
    if (!(await confirmDialog({ title: "Cancel this render batch?", body: "Jobs still in progress will stop.", danger: true, confirmLabel: "Cancel batch" }))) return;
    setBatches((bs) => bs?.map((b) => (b._id === id ? { ...b, status: "canceled" as RenderBatchStatus } : b)) ?? bs);
    await cancelBatch(id).catch(() => {});
  };

  const title: ReactNode = (
    <span className="flex items-center gap-2.5">
      Render queue
      {batches && batches.length > 0 && (
        <Chip tone="gold">
          {rendering} rendering · {queued} queued
        </Chip>
      )}
    </span>
  );

  return (
    <Modal open onClose={onClose} title={title} size="md" className="max-w-[560px]">
      {batches === null ? (
        <div className="flex h-40 items-center justify-center">
          <Loader label="Loading queue…" />
        </div>
      ) : batches.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <span className="grid h-12 w-12 place-items-center rounded-xl border border-ink-100 bg-ink-50 text-ink-400">
            <Inbox size={22} />
          </span>
          <p className="text-[13px] font-semibold text-ink-900">Nothing rendering</p>
          <p className="max-w-xs text-[12px] leading-snug text-ink-500">
            Queue a render from a model or the bulk render studio — batches show up here with live progress.
          </p>
        </div>
      ) : (
        <div>
          {batches.map((b) => (
            <QueueRow key={b._id} batch={b} onCancel={() => cancel(b._id)} />
          ))}
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-[var(--accent-border)] bg-[var(--accent-soft)] px-2.5 py-1.5 text-[11px] font-medium text-[var(--accent-strong)]">
            Renders finish even after you close the tab — you'll get a ping when they're done.
          </div>
        </div>
      )}
    </Modal>
  );
}

function QueueRow({ batch, onCancel }: { batch: RenderBatch; onCancel: () => void }) {
  const st = STATUS[batch.status];
  const pct = batch.totalJobs > 0 ? Math.round((batch.doneJobs / batch.totalJobs) * 100) : 0;
  const running = batch.status === "running";
  const cancelable = batch.status === "running" || batch.status === "queued";
  const ComputeIcon = batch.compute === "cloud" ? Cloud : Server;

  return (
    <div className="flex items-center gap-3 border-b border-[rgba(112,94,64,.10)] py-3 last:border-0">
      <span
        className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-[7px] text-white"
        style={{ background: "var(--accent)" }}
      >
        <ComputeIcon size={13} />
      </span>
      <div className="w-[180px] min-w-0">
        <b className="block truncate text-[12.5px] text-ink-900">
          Render batch · {batch.totalJobs} {batch.totalJobs === 1 ? "file" : "files"}
        </b>
        <div className="truncate text-[11.5px] text-ink-500">
          {batch.compute === "cloud" ? "Vault Cloud" : "On-premises"}
          {batch.resolution ? ` · ${batch.resolution}` : ""} · {relTime(batch.createdAt)}
        </div>
      </div>
      <div className="relative h-[7px] flex-1 overflow-hidden rounded-full border border-white/50 bg-white/40">
        <div
          className="relative h-full rounded-full"
          style={{ width: `${pct}%`, background: "linear-gradient(90deg,#C9A96A,var(--accent))" }}
        >
          {running && <span className="shimmer-bg absolute inset-0 opacity-70" />}
        </div>
      </div>
      <span className="flex w-[120px] shrink-0 items-center justify-end gap-1.5">
        {running ? (
          <span className="text-[11.5px] font-semibold text-[var(--accent-strong)]">
            {batch.doneJobs}/{batch.totalJobs}
          </span>
        ) : (
          <Chip tone={st.tone}>{st.label}</Chip>
        )}
        {cancelable && (
          <button
            onClick={onCancel}
            title="Cancel batch"
            className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-ink-400 hover:bg-[var(--red-soft,rgba(192,68,46,.12))] hover:text-[var(--red,#c0442e)]"
          >
            <X size={13} />
          </button>
        )}
      </span>
    </div>
  );
}
